// RunController: advances the simulation in ≤ 60 s sub-steps, raises decision points (spec §9), applies warp
// (spec §5) and produces the RunResult (spec §10). No DOM: the UI calls advance() from its own frame loop.
import type {
  CraftSpec, EngineSpec, ModSpec, Controls, SimState, Route, AirSegment, SeaSegment, Decision, RunEvent, RunResult,
  EndReason, Baseline, Warp, Domain, AirEnv, SeaEnv,
} from '../types';
import { WARP_LEVELS } from '../types';
import { resolveBuild, sanitizeControls, type ResolvedCraft } from '../physics/resolve';
import { initialState as airInitial, stepAircraft, optimumAltitudeFt, climbPoint } from '../physics/aircraft';
import { initialState as seaInitial, stepVessel } from '../physics/vessel';
import { predictRange } from '../physics/predict';
import { ftToM } from '../physics/atmosphere';
import { segmentIndexAt, airEnvFor, seaEnvFor } from './route';
import { efficiencyFor, efficiencyIndexFor, creditsFor } from './score';
import {
  topOfClimbDecision, stepClimbDecision, airConditionsDecision, seaConditionsDecision, weatherDecision, reserveDecision,
} from './decisions';

export interface RunOptions {
  craft: CraftSpec;
  engine: EngineSpec;
  mods: ModSpec[];
  controls: Controls;
  route: Route;
  baseline: Baseline;
  now?: () => Date;
}

export const MAX_SUBSTEP_S = 60;
/** Auto warp aims for a whole run in about four minutes of real time (spec §9). */
export const TARGET_RUN_REAL_S = 240;
/** Auto warp drops so that the last three real seconds before a known decision run at 1× (spec §9). */
const LEAD_REAL_S = 3;
const RESERVE_FRACTION = 0.1;
const STEP_CHECK_S = 600;

export class RunController {
  readonly rc: ResolvedCraft;
  readonly route: Route;
  readonly baseline: Baseline;
  readonly domain: Domain;
  readonly predictedHours: number;
  state: SimState;
  controls: Controls;
  pendingDecision: Decision | null = null;
  warp: Warp = 1;
  autoWarp = true;
  events: RunEvent[] = [];

  private endedBy: EndReason | null = null;
  private endedAt = '';
  private routeOffsetNm = 0;
  private penaltySegment = -1;
  private lastSegment = 0;
  private tocDone: boolean;
  private reserveDone = false;
  private offeredStepFt = -1;
  private declinedStepFt = -1;
  private nextStepCheckS = STEP_CHECK_S;
  private readonly capacityKg: number;
  private readonly now: () => Date;

  constructor(o: RunOptions) {
    this.rc = resolveBuild(o.craft, o.engine, o.mods);
    this.route = o.route;
    this.baseline = o.baseline;
    this.domain = o.craft.domain;
    this.now = o.now ?? (() => new Date());
    this.controls = sanitizeControls(this.rc, o.controls);
    this.predictedHours = predictRange(this.rc, this.controls).hours;
    this.state = this.rc.air ? airInitial(this.rc, this.controls) : seaInitial(this.rc, this.controls);
    this.capacityKg = this.rc.air ? this.rc.air.fuelCapacityKg : this.rc.sea ? this.rc.sea.fuelCapacityKg : 0;
    this.tocDone = !this.rc.air;
  }

  get ended(): boolean {
    return this.endedBy !== null;
  }

  /** Route position = distance flown less the extra distance of diverts. */
  get routePositionNm(): number {
    return this.state.distanceNm - this.routeOffsetNm;
  }

  get currentSegment(): AirSegment | SeaSegment {
    return this.route.segments[segmentIndexAt(this.route, this.routePositionNm)];
  }

  /** Advance by real seconds; sub-steps at ≤ 60 s of sim time. Idle while a decision is pending or the run has ended. */
  advance(dtRealS: number): void {
    if (this.pendingDecision || this.endedBy) return;
    let left = dtRealS * this.effectiveWarp();
    while (left > 1e-9 && !this.pendingDecision && !this.endedBy) {
      const dt = Math.min(MAX_SUBSTEP_S, left);
      this.step(dt);
      left -= dt;
    }
  }

  choose(optionId: string): void {
    const d = this.pendingDecision;
    if (!d) return;
    const opt = d.options.find((x) => x.id === optionId);
    if (!opt) throw new Error(`unknown option ${optionId}`);
    this.pendingDecision = null;
    this.log(`${d.title}: ${opt.label}`);
    if (opt.id === 'end-run') { this.endRun(); return; }
    if (d.kind === 'weather-cell') {
      const cell = this.currentSegment.weatherCell;
      if (cell && opt.id === 'divert') this.routeOffsetNm += cell.divertNm;
      else this.penaltySegment = segmentIndexAt(this.route, this.routePositionNm);
    }
    if (d.kind === 'step-climb' && opt.id === 'stay') this.declinedStepFt = this.offeredStepFt;
    if (opt.controls) {
      this.controls = sanitizeControls(this.rc, { ...this.controls, ...opt.controls });
      const target = opt.controls.altitudeFt;
      if (this.rc.air && target !== undefined && target > this.state.altitudeFt + 50) this.state = { ...this.state, phase: 'climb' };
    }
  }

  endRun(): void {
    if (this.endedBy) return;
    this.pendingDecision = null;
    this.finish('player');
    this.log(`Run ended by the player with ${Math.round(this.state.fuelKg)} kg of fuel left`);
  }

  result(): RunResult | null {
    if (!this.endedBy) return null;
    const s = this.state;
    const fuelUsedKg = this.capacityKg - s.fuelKg;
    const efficiency = efficiencyFor(this.domain, s.distanceNm, fuelUsedKg);
    const efficiencyIndex = efficiencyIndexFor(efficiency, this.baseline);
    return {
      craftId: this.rc.craft.id,
      modIds: this.rc.mods.map((m) => m.id),
      seed: this.route.seed,
      distanceNm: s.distanceNm,
      fuelUsedKg,
      simHours: s.timeS / 3600,
      efficiency,
      efficiencyIndex,
      credits: creditsFor(s.distanceNm, efficiencyIndex, this.baseline.rangeNm),
      endedBy: this.endedBy,
      events: this.events.slice(),
      isBest: false,
      endedAt: this.endedAt,
    };
  }

  // ---------------------------------------------------------------- warp

  /** Nearest warp level (in log space) that makes the predicted run last about TARGET_RUN_REAL_S. */
  autoLevel(): Warp {
    const want = Math.max(1, Math.min(3600, (this.predictedHours * 3600) / TARGET_RUN_REAL_S));
    let best: Warp = WARP_LEVELS[0];
    for (const level of WARP_LEVELS) if (Math.abs(Math.log(level / want)) < Math.abs(Math.log(best / want))) best = level;
    return best;
  }

  /** Sim seconds until the next decision point that can be foreseen (weather cell, reserve, top of climb), or null. */
  secondsToKnownDecision(): number | null {
    const s = this.state;
    let best: number | null = null;
    const consider = (v: number): void => { if (v >= 0 && (best === null || v < best)) best = v; };
    if (s.groundKt > 0) {
      const idx = segmentIndexAt(this.route, this.routePositionNm);
      for (let i = idx + 1; i < this.route.segments.length; i++) {
        const seg = this.route.segments[i];
        if (seg.weatherCell) { consider(((seg.fromNm - this.routePositionNm) / s.groundKt) * 3600); break; }
      }
    }
    if (!this.reserveDone && s.fuelFlowKgPerH > 0) consider(((s.fuelKg - RESERVE_FRACTION * this.capacityKg) / s.fuelFlowKgPerH) * 3600);
    if (this.rc.air && s.phase === 'climb') {
      const cp = climbPoint(this.rc, s.massKg, ftToM(s.altitudeFt), this.controls, s.storesJettisoned);
      if (cp.rocMs > 0) consider(ftToM(Math.max(0, (this.controls.altitudeFt ?? 0) - s.altitudeFt)) / cp.rocMs);
    }
    return best;
  }

  effectiveWarp(): number {
    if (!this.autoWarp) return this.warp;
    const level = this.autoLevel();
    const ahead = this.secondsToKnownDecision();
    if (ahead === null) return level;
    for (let i = WARP_LEVELS.length - 1; i >= 0; i--) {
      const l = WARP_LEVELS[i];
      if (l <= level && ahead >= LEAD_REAL_S * l) return l;
    }
    return 1;
  }

  // ---------------------------------------------------------------- stepping

  private envFor(idx: number): AirEnv | SeaEnv {
    const seg = this.route.segments[idx];
    const penalty = idx === this.penaltySegment && seg.weatherCell ? seg.weatherCell.pushPenalty : 0;
    return this.rc.air ? airEnvFor(seg as AirSegment, penalty) : seaEnvFor(seg as SeaSegment, penalty);
  }

  private step(dtS: number): void {
    const prev = this.state;
    const idx = segmentIndexAt(this.route, this.routePositionNm);
    const env = this.envFor(idx);
    const next = this.rc.air
      ? stepAircraft(this.rc, prev, this.controls, env as AirEnv, dtS)
      : stepVessel(this.rc, prev, this.controls, env as SeaEnv, dtS);
    this.state = { ...next, segmentIndex: segmentIndexAt(this.route, next.distanceNm - this.routeOffsetNm) };
    this.afterStep(prev, this.state);
  }

  private afterStep(prev: SimState, next: SimState): void {
    if (next.phase === 'ended') {
      this.finish('dry-tank');
      this.log(`Dry tank after ${Math.round(next.distanceNm)} nm`);
      return;
    }
    if (!prev.storesJettisoned && next.storesJettisoned) this.log('External tanks empty and jettisoned');
    if (!this.tocDone && prev.phase === 'climb' && next.phase === 'cruise') {
      this.tocDone = true;
      this.log(`Top of climb at ${Math.round(next.altitudeFt).toLocaleString('en-US')} ft`);
      this.raise(topOfClimbDecision(this.rc, next, this.controls, this.envFor(next.segmentIndex) as AirEnv));
      return;
    }
    if (!this.reserveDone && next.fuelKg <= RESERVE_FRACTION * this.capacityKg) {
      this.reserveDone = true;
      this.raise(reserveDecision(next, this.capacityKg, this.domain));
      return;
    }
    if (next.segmentIndex > this.lastSegment) {
      this.lastSegment = next.segmentIndex;
      if (this.enteredSegment(next, next.segmentIndex)) return;
    }
    if (this.rc.air && next.phase === 'cruise' && next.timeS >= this.nextStepCheckS) {
      this.nextStepCheckS = next.timeS + STEP_CHECK_S;
      const env = this.envFor(next.segmentIndex) as AirEnv;
      const opt = optimumAltitudeFt(this.rc, next, this.controls, env);
      if (opt >= next.altitudeFt + 2000 && opt !== this.declinedStepFt) {
        const d = stepClimbDecision(this.rc, next, this.controls, env, opt);
        if (d) { this.offeredStepFt = opt; this.raise(d); }
      }
    }
  }

  /** Returns true when a decision was raised. A conditions decision needs a real change from the previous segment. */
  private enteredSegment(s: SimState, idx: number): boolean {
    const seg = this.route.segments[idx];
    const prev = idx > 0 ? this.route.segments[idx - 1] : undefined;
    const no = idx + 1;
    if (this.rc.air) {
      const a = seg as AirSegment, p = prev as AirSegment | undefined;
      this.log(`Segment ${no}: wind ${a.windKtByLayer.map((w) => (w >= 0 ? '+' : '') + Math.round(w)).join(' / ')} kt by layer`);
      if (a.weatherCell) { this.raise(weatherDecision(a.weatherCell, 'air', no)); return true; }
      const changed = !p || a.windKtByLayer.some((w, i) => Math.abs(w - p.windKtByLayer[i]) >= 10);
      if (s.phase !== 'cruise' || !changed) return false;
      const d = airConditionsDecision(this.rc, s, this.controls, airEnvFor(a, 0), no);
      if (d) { this.raise(d); return true; }
      return false;
    }
    const m = seg as SeaSegment, p = prev as SeaSegment | undefined;
    this.log(`Segment ${no}: current ${m.currentKn >= 0 ? '+' : ''}${m.currentKn.toFixed(1)} kn, sea state ${m.seaState}`);
    if (m.weatherCell) { this.raise(weatherDecision(m.weatherCell, 'sea', no)); return true; }
    const changed = !p || Math.abs(m.currentKn - p.currentKn) >= 1 || Math.abs(m.seaState - p.seaState) >= 2;
    if (!changed) return false;
    const d = seaConditionsDecision(this.rc, s, this.controls, m, seaEnvFor(m, 0), no);
    if (d) { this.raise(d); return true; }
    return false;
  }

  private raise(d: Decision): void {
    this.pendingDecision = d;
  }

  private finish(reason: EndReason): void {
    this.endedBy = reason;
    this.endedAt = this.now().toISOString();
    this.state = { ...this.state, phase: 'ended' };
  }

  private log(text: string): void {
    this.events.push({ timeS: this.state.timeS, distanceNm: this.state.distanceNm, text });
  }
}
